import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, useMapEvents } from "react-leaflet";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ConfidenceBadge, StatusPill } from "@/components/badges";
import DirectionsButton from "@/components/DirectionsButton";
import { connectorLabels } from "@/lib/connectors";
import { XIcon, MapPinIcon, CameraIcon, PencilSimpleIcon, FloppyDiskIcon, CheckIcon, ProhibitIcon, ImageIcon } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CONNECTORS = [
  { id: "ccs2", label: "CCS2" }, { id: "type2", label: "Type 2" },
  { id: "bharat_ac_001", label: "Bharat AC-001" }, { id: "bharat_dc_001", label: "Bharat DC-001" },
];
const STATUSES = ["AVAILABLE", "CHARGING", "OUTOFORDER", "unknown"];
const ACCESS = ["Public", "Restricted", "Private"];
const HYD = { lat: 17.385, lng: 78.4867 };

function LocationPicker({ pos, setPos }) {
  useMapEvents({ click(e) { setPos({ lat: e.latlng.lat, lng: e.latlng.lng }); } });
  return pos ? <CircleMarker center={[pos.lat, pos.lng]} radius={9} pathOptions={{ color: "#059669", fillColor: "#059669", fillOpacity: 0.8, weight: 3 }} /> : null;
}

function PhotoGrid({ photos, canView }) {
  if (!canView) return <div className="text-xs text-slate-400">Photo evidence is private (analyst/admin only).</div>;
  if (!photos || photos.length === 0) return <div className="text-xs text-slate-400">No photos attached.</div>;
  return (
    <div className="grid grid-cols-3 gap-2">
      {photos.map((p) => (
        <a key={p.id} href={`${BACKEND}${p.url}`} target="_blank" rel="noreferrer" className="group relative block aspect-square overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
          <img src={`${BACKEND}${p.url}`} alt={p.filename || "evidence"} className="h-full w-full object-cover transition-transform group-hover:scale-105" />
          {p.gps && <span className="absolute bottom-0 left-0 right-0 bg-black/50 px-1 py-0.5 text-[9px] text-white truncate">{p.gps.lat?.toFixed(4)}, {p.gps.lng?.toFixed(4)}</span>}
        </a>
      ))}
    </div>
  );
}

function PhotoAttach({ photos, setPhotos, gps }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const onPick = async (files) => {
    setBusy(true);
    for (const file of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", file);
      if (gps) { fd.append("lat", gps.lat); fd.append("lng", gps.lng); }
      try {
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, data]);
      } catch (e) { toast.error("Photo upload failed"); }
    }
    setBusy(false);
  };
  return (
    <div>
      <input ref={ref} type="file" accept="image/*" multiple hidden onChange={(e) => onPick(e.target.files)} data-testid="photo-input" />
      <div className="grid grid-cols-3 gap-2">
        {photos.map((p) => (
          <div key={p.id} className="relative aspect-square overflow-hidden rounded-lg border border-slate-200">
            <img src={`${BACKEND}${p.url}`} alt={p.filename} className="h-full w-full object-cover" />
            <button type="button" onClick={() => setPhotos((ps) => ps.filter((x) => x.id !== p.id))} className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white"><XIcon size={12} /></button>
          </div>
        ))}
        <button type="button" data-testid="add-photo-btn" onClick={() => ref.current?.click()} disabled={busy}
          className="flex aspect-square flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-slate-300 text-slate-400 hover:border-emerald-400 hover:text-emerald-600 transition-colors disabled:opacity-50">
          <CameraIcon size={20} /><span className="text-[10px]">{busy ? "Uploading…" : "Add photo"}</span>
        </button>
      </div>
    </div>
  );
}

const Row = ({ label, children }) => (
  <div>
    <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);
const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100";

export default function StationPanel({ open, mode, siteId, onClose, onSaved, startEditing = false }) {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "analyst";
  const isAdmin = user?.role === "admin";
  const [sources, setSources] = useState([]);
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(mode === "create");
  const [saving, setSaving] = useState(false);
  const [pos, setPos] = useState(null);
  const [form, setForm] = useState({ name: "", address: "", connector_types: [], status: "AVAILABLE", operator: "", power_kw: "", tariff_per_kwh: "", source_id: "", access_type: "Public" });
  const [photos, setPhotos] = useState([]);

  useEffect(() => { if (open) api.get("/sources").then((r) => setSources(r.data)); }, [open]);

  useEffect(() => {
    if (!open) return;
    if (mode === "create") {
      setDetail(null); setEditing(true); setPhotos([]); setPos(null);
      setForm({ name: "", address: "", connector_types: [], status: "AVAILABLE", operator: "", power_kw: "", tariff_per_kwh: "", source_id: "", access_type: "Public" });
    } else if (siteId) {
      setEditing(!!startEditing);
      api.get(`/sites/${siteId}`).then((r) => {
        setDetail(r.data);
        const s = r.data.site;
        setPos({ lat: s.latitude, lng: s.longitude });
        setForm({ name: s.name || "", address: s.address || "", connector_types: s.connector_types || [], status: s.status || "unknown", operator: s.operator || "", power_kw: s.power_kw ?? "", tariff_per_kwh: s.tariff_per_kwh ?? "", source_id: (s.source_ids || [])[0] || "", access_type: s.access_type || "Public" });
        setPhotos([]);
      });
    }
  }, [open, mode, siteId, startEditing]);

  const toggleConn = (id) => setForm((f) => ({ ...f, connector_types: f.connector_types.includes(id) ? f.connector_types.filter((x) => x !== id) : [...f.connector_types, id] }));

  const submitCreate = async () => {
    if (!form.name) return toast.error("Station name is required");
    if (!pos) return toast.error("Drop a pin on the map to set the location");
    setSaving(true);
    try {
      const body = { name: form.name, address: form.address || null, latitude: pos.lat, longitude: pos.lng,
        connector_types: form.connector_types, status: form.status, operator: form.operator || null,
        power_kw: form.power_kw === "" ? null : Number(form.power_kw), tariff_per_kwh: form.tariff_per_kwh === "" ? null : Number(form.tariff_per_kwh),
        source_id: form.source_id || null, access_type: form.access_type, photo_ids: photos.map((p) => p.id) };
      const { data } = await api.post("/sites", body);
      toast.success(data.review_status === "pending" ? "Submitted for admin review" : "Station published to the map");
      onSaved && onSaved(data); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to add station"); } finally { setSaving(false); }
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const body = { name: form.name, address: form.address || null, latitude: pos?.lat, longitude: pos?.lng,
        connector_types: form.connector_types, status: form.status, operator: form.operator || null,
        power_kw: form.power_kw === "" ? null : Number(form.power_kw), tariff_per_kwh: form.tariff_per_kwh === "" ? null : Number(form.tariff_per_kwh),
        access_type: form.access_type, add_photo_ids: photos.map((p) => p.id) };
      const { data } = await api.patch(`/sites/${siteId}`, body);
      toast.success("Station updated");
      setEditing(false); setPhotos([]);
      const r = await api.get(`/sites/${siteId}`); setDetail(r.data);
      onSaved && onSaved(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed"); } finally { setSaving(false); }
  };

  const review = async (action) => {
    try { await api.post(`/sites/${siteId}/review`, { action }); toast.success(`Station ${action}d`); onSaved && onSaved(); onClose(); }
    catch (e) { toast.error("Review action failed"); }
  };

  if (!open) return null;
  const s = detail?.site;
  const c = s?.confidence;
  const showForm = mode === "create" || editing;

  return (
    <div className="fixed inset-0 z-[1200] flex justify-end">
      <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm" onClick={onClose} />
      <div data-testid="station-panel" className="relative flex h-full w-full max-w-md flex-col bg-white shadow-2xl animate-in slide-in-from-right">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 h-16 shrink-0">
          <div>
            <h2 className="font-display text-lg font-semibold text-slate-900">{mode === "create" ? "Add Station" : (editing ? "Edit Station" : s?.name || "Station")}</h2>
            {mode !== "create" && s && <div className="text-xs text-slate-400">{s.provenance_label} · {s.review_status || "published"}</div>}
          </div>
          <button data-testid="panel-close" onClick={onClose} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100"><XIcon size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {mode !== "create" && s && !editing && (
            <div className="flex items-center gap-2">
              <StatusPill status={s.status} /><ConfidenceBadge label={c?.label} score={c?.overall_score} />
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{s.access_type || "Public"}</span>
            </div>
          )}
          {mode !== "create" && s && !editing && (
            <DirectionsButton lat={s.latitude} lng={s.longitude} full />
          )}

          {(mode === "create" || editing) ? (
            <>
              <Row label="Station name"><input data-testid="field-name" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Madhapur Metro Bay" /></Row>
              <Row label="Address"><input data-testid="field-address" className={inputCls} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Area, City" /></Row>
              <Row label={`Location — tap map to drop pin${pos ? ` (${pos.lat.toFixed(4)}, ${pos.lng.toFixed(4)})` : ""}`}>
                <div className="overflow-hidden rounded-lg border border-slate-200" data-testid="pin-map">
                  {(mode === "create" || pos) ? (
                    <MapContainer key={siteId || "create"} center={[pos?.lat || HYD.lat, pos?.lng || HYD.lng]} zoom={pos ? 14 : 12} style={{ height: 200, width: "100%" }} scrollWheelZoom={false}>
                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                      <LocationPicker pos={pos} setPos={setPos} />
                    </MapContainer>
                  ) : (
                    <div style={{ height: 200 }} className="flex items-center justify-center bg-slate-50 text-xs text-slate-400">Loading map…</div>
                  )}
                </div>
              </Row>
              <Row label="Connector types">
                <div className="flex flex-wrap gap-2">
                  {CONNECTORS.map((c2) => (
                    <button type="button" key={c2.id} data-testid={`conn-${c2.id}`} onClick={() => toggleConn(c2.id)}
                      className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${form.connector_types.includes(c2.id) ? "border-emerald-400 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600"}`}>{c2.label}</button>
                  ))}
                </div>
              </Row>
              <div className="grid grid-cols-2 gap-3">
                <Row label="Status"><select data-testid="field-status" className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>{STATUSES.map((x) => <option key={x}>{x}</option>)}</select></Row>
                <Row label="Access type"><select data-testid="field-access" className={inputCls} value={form.access_type} onChange={(e) => setForm({ ...form, access_type: e.target.value })}>{ACCESS.map((x) => <option key={x}>{x}</option>)}</select></Row>
                <Row label="Operator"><input className={inputCls} value={form.operator} onChange={(e) => setForm({ ...form, operator: e.target.value })} /></Row>
                <Row label="Power (kW)"><input type="number" className={inputCls} value={form.power_kw} onChange={(e) => setForm({ ...form, power_kw: e.target.value })} /></Row>
                <Row label="Tariff ₹/kWh"><input type="number" step="0.1" className={inputCls} value={form.tariff_per_kwh} onChange={(e) => setForm({ ...form, tariff_per_kwh: e.target.value })} /></Row>
                <Row label="Data source"><select data-testid="field-source" className={inputCls} value={form.source_id} onChange={(e) => setForm({ ...form, source_id: e.target.value })}><option value="">—</option>{sources.map((so) => <option key={so.id} value={so.id}>{so.name}</option>)}</select></Row>
              </div>
              <Row label="Photos (stored with GPS + timestamp)"><PhotoAttach photos={photos} setPhotos={setPhotos} gps={pos} /></Row>
            </>
          ) : (
            <>
              <Row label="Address"><div className="text-sm text-slate-800">{s?.address || "—"}</div></Row>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><div className="text-[11px] uppercase text-slate-400">Operator</div><div className="text-slate-800">{s?.operator || "—"}</div></div>
                <div><div className="text-[11px] uppercase text-slate-400">Power</div><div className="text-slate-800 font-mono">{s?.power_kw ? `${s.power_kw} kW` : "—"}</div></div>
                <div><div className="text-[11px] uppercase text-slate-400">Tariff ₹/kWh</div><div className="text-slate-800 font-mono">{s?.tariff_per_kwh ?? "—"}</div></div>
                <div><div className="text-[11px] uppercase text-slate-400">Connectors</div><div className="text-slate-800 flex items-center gap-1.5">{connectorLabels(s?.connector_types) || "—"}{s?.connector_needs_review && <span className="rounded bg-amber-50 border border-amber-200 px-1 py-0.5 text-[9px] font-semibold text-amber-700">REVIEW</span>}</div></div>
                <div><div className="text-[11px] uppercase text-slate-400">Coordinates</div><div className="text-slate-800 font-mono text-xs">{s?.latitude?.toFixed(5)}, {s?.longitude?.toFixed(5)}</div></div>
                <div><div className="text-[11px] uppercase text-slate-400">Access</div><div className="text-slate-800">{s?.access_type || "Public"}</div></div>
              </div>
              <Row label="Photo evidence"><PhotoGrid photos={s?.photos} canView={canEdit} /></Row>
              {detail?.audits?.some((a) => (a.photos || []).length) && (
                <Row label="Audit photos">
                  {detail.audits.filter((a) => (a.photos || []).length).map((a) => (
                    <div key={a.id} className="mb-2"><div className="mb-1 text-xs text-slate-400">{a.auditor_name}</div><PhotoGrid photos={a.photos} canView={canEdit} /></div>
                  ))}
                </Row>
              )}
              <Row label="Confidence reasons">
                <ul className="space-y-1 text-xs text-slate-500">{(c?.reasons || []).map((r, i) => <li key={i} className="flex gap-1.5"><span className="text-emerald-500">•</span>{r}</li>)}</ul>
              </Row>
            </>
          )}
        </div>

        <div className="shrink-0 border-t border-slate-100 p-4 space-y-2">
          {mode === "create" ? (
            <button data-testid="submit-station" onClick={submitCreate} disabled={saving} className="w-full rounded-lg bg-emerald-600 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
              {saving ? "Saving…" : (isAdmin ? "Publish station" : "Submit for review")}
            </button>
          ) : editing ? (
            <div className="flex gap-2">
              <button onClick={() => setEditing(false)} className="flex-1 rounded-lg border border-slate-300 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
              <button data-testid="save-edit" onClick={saveEdit} disabled={saving} className="flex-[2] inline-flex items-center justify-center gap-1.5 rounded-lg bg-emerald-600 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"><FloppyDiskIcon size={16} />{saving ? "Saving…" : "Save changes"}</button>
            </div>
          ) : (
            <div className="space-y-2">
              {canEdit && <button data-testid="edit-station" onClick={() => setEditing(true)} className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 py-2.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-100"><PencilSimpleIcon size={16} /> Edit fields inline</button>}
              {isAdmin && s?.review_status === "pending" && (
                <div className="flex gap-2">
                  <button data-testid="approve-station" onClick={() => review("approve")} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-emerald-600 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700"><CheckIcon size={16} /> Approve</button>
                  <button data-testid="reject-station" onClick={() => review("reject")} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-rose-600 py-2.5 text-sm font-semibold text-white hover:bg-rose-700"><ProhibitIcon size={16} /> Reject</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
